// Everything about the selection frame: where it sits, what grabs it, and how
// it paints. The frame is drawn padded, so the drawn rect and the hit rect have
// to agree — keeping both here is what keeps them agreeing.

import type { IScreenRect } from '@utils/svgMarkers';
import { hitTestItem, itemRectPx } from '../../../utils/markupHitTest';
import type { IMarkupPoint, MarkupItem } from '../../../utils/markupTypes';

/** How close (px) the eraser must get to an item's line to grab it. */
export const ERASE_TOLERANCE_PX = 8;
/** Slack around a text stamp's box for selecting it. */
export const SELECT_TOLERANCE_PX = 4;
/** The resize handle's size (px) and its grab slack. */
const HANDLE_PX = 8;
const HANDLE_TOLERANCE_PX = 8;
/** Padding (px) around a selected item's dashed frame — also its click-to-drag margin. */
const SELECTION_FRAME_PAD = 4;
/** The smallest a stroke may be scaled down to, % of content width. */
export const MIN_STROKE_RESIZE_PCT = 2;

/** The topmost text box under the point — the selection target. */
export function topmostBoxHit(items: readonly MarkupItem[], p: IMarkupPoint, box: IScreenRect): number | null {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i]!;
    if (item.kind === 'text' && hitTestItem(item, p, box.width, box.height, SELECT_TOLERANCE_PX)) return i;
  }
  return null;
}

function accentColor(): string {
  return getComputedStyle(document.documentElement).getPropertyValue('--sidebar-accent').trim();
}

/** Dashed frame around a selected item. Solo selections also get a square
 *  resize handle on the frame's right edge (drag target for width changes;
 *  strokes scale) — a multi-selection has no single width to resize. */
export function drawSelectionFrame(
  ctx: CanvasRenderingContext2D,
  item: MarkupItem,
  width: number,
  height: number,
  showHandle: boolean,
): void {
  const box = itemRectPx(item, width, height);
  const pad = SELECTION_FRAME_PAD;
  const accent = accentColor();

  ctx.save();
  ctx.strokeStyle = accent;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 3]);
  ctx.strokeRect(box.left - pad, box.top - pad, box.w + pad * 2, box.h + pad * 2);
  ctx.setLineDash([]);
  if (showHandle) {
    ctx.fillStyle = accent;
    ctx.fillRect(box.left + box.w + pad - HANDLE_PX / 2, box.top + box.h / 2 - HANDLE_PX / 2, HANDLE_PX, HANDLE_PX);
  }
  ctx.restore();
}

/** Dashed marquee rectangle, live while a select-tool drag is picking items. */
export function drawMarquee(ctx: CanvasRenderingContext2D, rect: { left: number; top: number; w: number; h: number }): void {
  ctx.save();
  ctx.strokeStyle = accentColor();
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 3]);
  ctx.globalAlpha = 0.12;
  ctx.fillStyle = accentColor();
  ctx.fillRect(rect.left, rect.top, rect.w, rect.h);
  ctx.globalAlpha = 1;
  ctx.strokeRect(rect.left, rect.top, rect.w, rect.h);
  ctx.restore();
}

/** AABB overlap test, both rects in the same px space. */
export function rectsIntersect(
  a: { left: number; top: number; w: number; h: number },
  b: { left: number; top: number; w: number; h: number },
): boolean {
  return a.left < b.left + b.w && a.left + a.w > b.left && a.top < b.top + b.h && a.top + a.h > b.top;
}

/** Whether `p` falls inside `item`'s dashed selection frame — the same
 *  padded box that's drawn, so a selected item is draggable from anywhere
 *  inside it, not just its thin outline (a hollow rect/ellipse only hit-tests
 *  near its edge otherwise). */
export function pointInsideSelectionFrame(item: MarkupItem, p: IMarkupPoint, box: IScreenRect): boolean {
  const rect = itemRectPx(item, box.width, box.height);
  const target = { x: (p.x / 100) * box.width, y: (p.y / 100) * box.height };
  return (
    target.x >= rect.left - SELECTION_FRAME_PAD &&
    target.x <= rect.left + rect.w + SELECTION_FRAME_PAD &&
    target.y >= rect.top - SELECTION_FRAME_PAD &&
    target.y <= rect.top + rect.h + SELECTION_FRAME_PAD
  );
}

/** Whether the point grabs the selected item's resize handle. */
export function hitsResizeHandle(
  item: MarkupItem,
  p: IMarkupPoint,
  box: IScreenRect,
): boolean {
  const rect = itemRectPx(item, box.width, box.height);
  const handleX = rect.left + rect.w + 4;
  const handleY = rect.top + rect.h / 2;
  const target = { x: (p.x / 100) * box.width, y: (p.y / 100) * box.height };
  return Math.abs(target.x - handleX) <= HANDLE_TOLERANCE_PX && Math.abs(target.y - handleY) <= HANDLE_TOLERANCE_PX + HANDLE_PX;
}

/** A stroke's raw (unpadded) horizontal bounds in %, for resize math. */
export function strokeBoundsPct(points: readonly IMarkupPoint[]): { left: number; w: number } {
  const xs = points.map((p) => p.x);
  const left = Math.min(...xs);
  return { left, w: Math.max(...xs) - left };
}
